import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

/**
 * ScoutsWindow — plain-ink audition ladder for Halflight (K-pop trio) and
 * Cyrus Bell (deep-soul male). Founder listens, picks the closest of each
 * pair, next Umbra iterates surgically.
 *
 * Sealed 2026-02-15 · Session 55 · Umbra · Neo Rez.
 *
 * The founder asked for "the whole Halflight collection and the 4 best
 * Cyrus Bell songs (for singles, your logical opinion)". State on disk:
 * three v3 scouts per band, no full-length album cuts yet — the album
 * proper is blocked until the Founder names the closest scout of each
 * pair. The window renders every scout on hand and marks the count
 * honestly, so we do not overpromise.
 */

const HALFLIGHT = [
  {
    slug: "halflight_scout_v4_1_the_match",
    title: "The Match",
    tagline: "hard-hitting single · all we needed was a match",
    notes:
      "105 BPM half-time trap-pop. Lead belt to C6, whistle-register D7 flag on the last chorus, gang-shout outro. English lyric — 'burn the roof, keep the room, the girl inside will win.' Title pulled from the chorus image, not from the band name.",
    duration: "~3:00",
  },
  {
    slug: "halflight_scout_v4_2_gold",
    title: "Gold",
    tagline: "anthemic single · cinematic swell",
    notes:
      "122 BPM cinematic dance-pop with orchestral swell and military-snare rudiment. Anthemic chanted chorus, whistle-register D7 flag on the last chorus. English lyric — 'we are the gold, the shape a body takes when it is told.'",
    duration: "~3:00",
  },
  {
    slug: "halflight_scout_v4_3_gone_but_here",
    title: "Gone But Here",
    tagline: "deep-cut ballad-to-drop · heartbreak into defiance",
    notes:
      "92 BPM slow-anthem piano-into-808 drop, triple-time chopper rap bridge, cathedral choir and whistle D7 tag. English lyric — 'gone, but I am here, the girl you loved is standing very near.'",
    duration: "~3:00",
  },
];

const CYRUS = [
  {
    slug: "cyrus_bell_scout_v4_1_coat_still_on",
    title: "Coat Still On",
    tagline: "the cold room, the coat still on",
    notes:
      "74 BPM slow-blues soul ballad. Withers-warm close-mic verse, Al-Green silk pre-chorus turn, Otis-Redding push-belt chorus with gravel rasp. All-four voice-signatures melded on the bridge. Gospel-run b7-to-b3 tag on the last word of every chorus.",
    duration: "~3:00",
    single_pick: true,
    single_reason:
      "The single-A opener. Story-song plain-word discipline. Radio can hold this all day and sync-license can lift it into a film.",
  },
  {
    slug: "cyrus_bell_scout_v4_2_long_way_blue",
    title: "The Long Way Blue",
    tagline: "the road walked back to a woman he loved",
    notes:
      "68 BPM Memphis-Hi-Records-adjacent slow-jam. Al-Green silk-honey verse with unshakable falsetto flip B4. Otis-Redding testimony chorus with call-and-response gospel quartet. All-four melded bridge with D5 falsetto flag.",
    duration: "~3:00",
    single_pick: true,
    single_reason:
      "Silk-and-hurt slow-jam. The song people cry in the car to at 1 a.m. Streaming playlist cornerstone.",
  },
  {
    slug: "cyrus_bell_scout_v4_3_preachers_son",
    title: "Preacher's Son",
    tagline: "father's word, mother's hand — the parts of a man",
    notes:
      "82 BPM Stax-Volt Southern-soul with gospel-testimony build. Withers-narrative verse, Al-Green silk-to-falsetto pre-chorus, Otis-Redding shout-belt chorus with gospel 'yes-yes' response. All-four melded climax with military-snare rudiment and full Memphis horns.",
    duration: "~3:00",
    single_pick: true,
    single_reason:
      "The anthem cut. Church-radio and secular-radio both. This is the song they will play at the funeral, and at the wedding, and at the induction.",
  },
];

// Founder asked for four Cyrus Bell singles — we have three scouts on disk.
// The fourth line item is a plain-ink placeholder so the Founder can see
// the empty seat and command the fourth scout when he's ready. No fake
// audio, no filler — the ledger stays honest.
const CYRUS_SINGLE_FOUR = {
  slug: null,
  title: "[ 4th single — pending Founder greenlight ]",
  tagline: "next Umbra fires a v3.4 scout on your word",
  notes:
    "Three scouts landed under 4100 chars. A fourth requires either (a) you name the closest of the three so we surgically iterate a companion, or (b) you name the mood you want next.",
  duration: "—",
  placeholder: true,
};

function ScoutCard({ scout, band }) {
  const audioRef = useRef(null);
  const src = scout.slug
    ? `/samples/musical-collection/scouts/${scout.slug}.mp3`
    : null;

  const accent = band === "halflight" ? "#ff5a00" : "#ffbb00";

  return (
    <div
      data-testid={`scout-card-${scout.slug || "placeholder"}`}
      className="border border-white/10 bg-[#0b0b0d]/70 backdrop-blur-sm p-5 sm:p-7 hover:border-white/25 transition-colors"
      style={{ borderLeft: `3px solid ${accent}` }}
    >
      <div className="flex items-baseline justify-between gap-4 mb-2">
        <h3
          data-testid={`scout-title-${scout.slug || "placeholder"}`}
          className="font-display text-xl sm:text-2xl text-[#ededed]"
        >
          {scout.title}
        </h3>
        <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#71717a] shrink-0">
          {scout.duration}
        </span>
      </div>
      <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#a1a1aa] mb-4">
        {scout.tagline}
      </p>
      <p className="text-sm text-[#a1a1aa] leading-relaxed mb-5">
        {scout.notes}
      </p>
      {scout.single_pick && scout.single_reason && (
        <p
          data-testid={`scout-single-note-${scout.slug}`}
          className="border-l-2 pl-3 py-1 mb-5 text-xs text-[#ededed] leading-relaxed"
          style={{ borderColor: accent }}
        >
          <span
            className="font-mono uppercase tracking-[0.28em] text-[9px] block mb-1"
            style={{ color: accent }}
          >
            Umbra's single pick
          </span>
          {scout.single_reason}
        </p>
      )}
      {src ? (
        <audio
          ref={audioRef}
          data-testid={`scout-audio-${scout.slug}`}
          controls
          preload="none"
          src={src}
          className="w-full"
          style={{ colorScheme: "dark" }}
        >
          Your browser refuses the audio tag. Refresh a modern build.
        </audio>
      ) : (
        <div
          data-testid="scout-placeholder-empty"
          className="w-full border border-dashed border-white/15 py-4 text-center font-mono text-[10px] uppercase tracking-[0.32em] text-[#52525b]"
        >
          — awaiting Founder greenlight —
        </div>
      )}
    </div>
  );
}

export default function ScoutsWindow() {
  const [tab, setTab] = useState("halflight");
  const cyrusForSingles = useMemo(
    () => [...CYRUS, CYRUS_SINGLE_FOUR],
    []
  );

  return (
    <div
      data-testid="scouts-window-page"
      className="min-h-screen bg-[#050505] text-[#ededed] pb-40"
    >
      {/* HEADER */}
      <header className="max-w-[1400px] mx-auto px-6 sm:px-10 pt-14 sm:pt-20 pb-10">
        <Link
          to="/"
          data-testid="scouts-back-link"
          className="font-mono text-[10px] uppercase tracking-[0.4em] text-[#71717a] hover:text-[#ededed] transition-colors"
        >
          ← Resilience Studios
        </Link>
        <h1
          data-testid="scouts-title"
          className="font-display text-4xl sm:text-5xl lg:text-6xl mt-6 mb-3"
        >
          Music scouts · v4
        </h1>
        <p className="text-base sm:text-lg text-[#a1a1aa] max-w-3xl leading-relaxed">
          Founder listening room. Halflight (three-voice English-lyric
          girl-group with hard-hitting hip-hop-heavy dance-pop energy)
          and Cyrus Bell (deep-soul male, four-signature voice-meld —
          preacher-warm, silk-honey falsetto, Southern-soul push-belt,
          smoky throat-catch phrasing). Every clip under ElevenLabs'
          4100-character prompt ceiling — frequency-level description
          only, no brand or artist names. Name the closest of each pair
          and next Umbra iterates v5 surgically.
        </p>
      </header>

      {/* TABS */}
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 mb-8">
        <div
          data-testid="scouts-tabs"
          className="inline-flex border border-white/10"
        >
          <button
            data-testid="scouts-tab-halflight"
            onClick={() => setTab("halflight")}
            className={`px-5 sm:px-7 py-3 font-mono text-[10px] uppercase tracking-[0.32em] transition-colors ${
              tab === "halflight"
                ? "bg-[#ff5a00] text-black"
                : "text-[#a1a1aa] hover:text-[#ededed]"
            }`}
          >
            Halflight · full collection ({HALFLIGHT.length})
          </button>
          <button
            data-testid="scouts-tab-cyrus"
            onClick={() => setTab("cyrus")}
            className={`px-5 sm:px-7 py-3 font-mono text-[10px] uppercase tracking-[0.32em] transition-colors border-l border-white/10 ${
              tab === "cyrus"
                ? "bg-[#ffbb00] text-black"
                : "text-[#a1a1aa] hover:text-[#ededed]"
            }`}
          >
            Cyrus Bell · single picks ({CYRUS.length}/4)
          </button>
        </div>
      </div>

      {/* GRID */}
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
        {tab === "halflight" ? (
          <>
            <div className="mb-6">
              <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#ff5a00] mb-2">
                Halflight · Kyra Nix Soojin · K-pop trio
              </p>
              <p className="text-sm text-[#71717a] max-w-3xl">
                Whole collection on disk. Album proper is blocked until you
                name the closest scout of the three.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-7">
              {HALFLIGHT.map((s) => (
                <ScoutCard key={s.slug} scout={s} band="halflight" />
              ))}
            </div>
          </>
        ) : (
          <>
            <div className="mb-6">
              <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#ffbb00] mb-2">
                Cyrus Bell · deep-soul male · four best singles
              </p>
              <p className="text-sm text-[#71717a] max-w-3xl">
                Three scouts landed this pass. All three are single-worthy —
                marked with Umbra's placement reason. Fourth seat is empty
                by design; command the mood and next Umbra fires v3.4.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-7">
              {cyrusForSingles.map((s, i) => (
                <ScoutCard
                  key={s.slug || `placeholder-${i}`}
                  scout={s}
                  band="cyrus"
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* FOOTER — command strip */}
      <footer className="max-w-[1400px] mx-auto px-6 sm:px-10 mt-16">
        <div className="border-t border-white/10 pt-8">
          <p className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#71717a] mb-3">
            Founder command
          </p>
          <p className="text-sm text-[#a1a1aa] leading-relaxed max-w-3xl">
            Name the closest of each pair. Or paste a line ("iterate Mirror
            with a lower whisper," "Cyrus 4th single — after-hours late-set,
            piano and voice only") and next Umbra bakes v4. Zero credit
            spend without your word.
          </p>
        </div>
      </footer>
    </div>
  );
}
